<template>
  <main>
    <section class="contact-form-header jm-bg-background-secondary">
      <h1 class="jm-uppercase jm-mb-3">
        En savoir plus sur l'activité de Styliste
      </h1>
      <p>
        Renseigne quelques informations puis découvre plusieurs profils de
        Stylistes.<br />
        Tu pourras ainsi échanger directement avec une personne exerçant déjà
        cette activité.<br />
        Aucun engagement, cela te permet de poser toutes tes questions !
      </p>
    </section>
    <section
      class="contact-form jm-m-3"
      v-if="profile"
      data-test="contact-form"
    >
      <form class="contact-form">
        <h2>Formulaire</h2>

        <div class="jm-mb-3">
          <BaseInput
            v-model="profile.firstname"
            @blur="v$.profile.firstname.$touch"
            label="Prénom*"
            placeholder="Prénom"
            type="text"
            data-test="form-firstname"
          />
          <div v-if="v$.profile.firstname.$error" class="contact-form-error">
            {{ getMessage('firstname') }}
          </div>
        </div>

        <div class="jm-mb-3">
          <BaseInput
            v-model="profile.lastname"
            @blur="v$.profile.lastname.$touch"
            label="Nom*"
            placeholder="Nom"
            type="text"
            data-test="form-lastname"
          />
          <div v-if="v$.profile.lastname.$error" class="contact-form-error">
            {{ getMessage('lastname') }}
          </div>
        </div>

        <BaseRadioGroup
          label="Êtes-vous déjà en contact avec une Styliste ?"
          v-model="isAlreadyAssigned"
          name="isAlreadyAssigned"
          :options="radioOptions"
          data-test="is-already-assigned-radio"
        />

        <div class="jm-mb-4" v-if="hasCountries && !isAlreadyAssigned">
          <BaseSelect
            :options="countries"
            v-model="profile.country"
            label="Pays de résidence*"
            data-test="form-country"
          />
        </div>

        <div class="jm-mb-3" v-if="isFrance && !isAlreadyAssigned">
          <BaseInput
            v-model="profile.postalCode"
            @blur="v$.profile.postalCode.$touch"
            label="Code Postal*"
            placeholder="Code Postal"
            type="text"
            data-test="form-postal"
          />
          <div v-if="v$.profile.postalCode.$error" class="contact-form-error">
            {{ getMessage('postalCode') }}
          </div>
        </div>

        <div class="jm-mb-4" v-if="!isAlreadyAssigned">
          <BaseSelect
            :options="discoverOptions"
            v-model="profile.discover"
            @blur="v$.profile.discover.$touch"
            label="Comment nous as-tu connu ?*"
            data-test="form-discover"
          />
          <div v-if="v$.profile.discover.$error" class="contact-form-error">
            {{ getMessage('discover') }}
          </div>
        </div>

        <div class="jm-mb-4">
          <BaseCheckBoxGroup
            label="Tu es intéressé par*"
            :options="interestOptions"
            v-model="profile.interests"
            name="interests"
            data-test="form-interests"
          />
        </div>

        <BaseRadioGroup
          label="As-tu déjà fait de la VDI (Vendeur à Domicile Indépendant) ?"
          v-model="profile.hasBeenVdi"
          name="hasBeenVdi"
          :options="radioOptions"
          data-test="form-has-been-vdi"
        />

        <div class="jm-mb-3">
          <BaseInput
            v-model="profile.email"
            @blur="v$.profile.email.$touch"
            label="Email*"
            placeholder="Email"
            type="text"
            data-test="form-email"
          />
          <div v-if="v$.profile.email.$error" class="contact-form-error">
            {{ getMessage('email') }}
          </div>
        </div>

        <div class="jm-mb-3">
          <BaseInput
            v-model="profile.phone"
            @blur="v$.profile.phone.$touch"
            label="Numéro de téléphone*"
            placeholder="0123456789"
            type="tel"
            data-test="form-tel"
          />
          <div v-if="v$.profile.phone.$error" class="contact-form-error">
            {{ getMessage('phone') }}
          </div>
        </div>

        <div class="jm-mb-4">
          <BaseInput
            v-model="profile.socialNetwork"
            label="Pseudo Facebook et/ou Instagram"
            placeholder="Pseudo"
            type="text"
            data-test="form-fb"
          />
        </div>

        <div class="jm-mb-5">
          <BaseButton
            type="submit"
            data-test="submit-btn"
            :disabled="disabledButton"
            :loading="loadingSubmit"
            @click="validate"
            >Valider</BaseButton
          >
        </div>
        <p class="rgpd-content">
          Vous êtes informé(e) que vos informations personnelles renseignées
          dans ce formulaire sont traitées par Beauty Blend, agissant en qualité
          de responsable de traitement, afin de vous envoyer des informations
          concernant l’activité de Styliste et de vous mettre en contact avec un
          Styliste. Conformément aux termes et conditions du Règlement (UE)
          2016/679 du 27 avril 2016 (dit « RGPD ») et de la Loi n°78-17 du 6
          janvier 1978 modifiée (dite « Loi informatique et libertés »), vous
          bénéficiez d’un droit d’accès, de rectification et, le cas échéant,
          d’un droit à l’effacement de vos données, ainsi que d’un droit
          d’opposition au traitement ou à sa limitation en vous adressant par
          courriel au DPO à l’adresse dpo@jolimoi.com. Pour plus d’informations,
          nous vous invitons à consulter notre Politique de confidentialité.”
        </p>
      </form>
    </section>

    <BaseModal
      v-if="showWebinarModal"
      @closeModal="showWebinarModal = false"
      data-test="webinar-modal"
    >
      <p class="jm-text-center jm-mt-0 jm-mb-4">
        Tu as indiqué être déjà en contact avec un Styliste.<br />
        Contacte-le pour poser tes questions.<br />
        Tu peux aussi assister à une réunion en ligne avec l'équipe Jolimoi et
        échanger ainsi à plusieurs.
      </p>
      <a :href="webinarUrl" class="jm-text-decoration-none" target="_blank">
        <BaseButton>{{ webinarLabelButton }}</BaseButton>
      </a>
    </BaseModal>
  </main>
</template>

<script>
import { useToast } from 'vue-toastification'
import useVuelidate from '@vuelidate/core'

import * as store from '@/store/store'
import { getCountries } from '@/services/api'
import {
  validatorAlphaNum,
  validatorEmail,
  validatorMinLength,
  validatorNumeric,
  validatorRequired,
  validatorRequiredIf
} from '@/helpers/formValidators'

import { gaTrackByEvent } from '@/helpers/gaTrack'
import BaseButton from '@/components/molecules/BaseButton.vue'
import BaseCheckBoxGroup from '@/components/molecules/form/BaseCheckBoxGroup.vue'
import BaseInput from '@/components/molecules/form/BaseInput.vue'
import BaseModal from '@/components/organisms/BaseModal.vue'
import BaseRadioGroup from '@/components/molecules/form/BaseRadioGroup'
import BaseSelect from '@/components/molecules/form/BaseSelect.vue'

const FRANCE_COUNTRY_ID = 8
const WEBINAR_URL =
  'https://app.livestorm.co/beauty-blend/devenir-styliste-beaute-jolimoi?type=light'
const WEBINAR_LABEL_BUTTON = 'Je réserve un créneau'

const discoverOptions = [
  'Instagram',
  'Facebook',
  'Une autre Styliste',
  'Un moteur de recherche',
  'Pinterest',
  'Podcast',
  'A la télévision',
  'Linkedin',
  'Un article dans la presse',
  'Youtube',
  'Snapchat',
  'TikTok',
  'Autres'
]

const interestOptions = [
  { value: 'supplements', label: 'Compléments alimentaires' },
  { value: 'body', label: 'Soin du corps' },
  { value: 'skincare', label: 'Soin du visage' },
  { value: 'makeup', label: 'Maquillage' },
  { value: 'hair', label: 'Cheveux' }
]

export default {
  name: 'ContactFormView',
  components: {
    BaseInput,
    BaseRadioGroup,
    BaseSelect,
    BaseButton,
    BaseModal,
    BaseCheckBoxGroup
  },
  setup() {
    const toast = useToast()
    return { toast, v$: useVuelidate() }
  },
  data() {
    return {
      loadingSubmit: false,
      radioOptions: [
        { label: 'Oui', value: true },
        { label: 'Non', value: false }
      ],
      countries: [],
      postalCode: [],
      isAlreadyAssigned: false,
      profile: undefined,
      webinarUrl: WEBINAR_URL,
      showWebinarModal: false,
      webinarLabelButton: WEBINAR_LABEL_BUTTON,
      interestOptions
    }
  },
  validations() {
    return {
      profile: {
        firstname: {
          ...validatorRequired
        },
        lastname: { ...validatorRequired },
        country: {
          ...validatorRequiredIf(this.hasCountries && !this.isAlreadyAssigned)
        },
        postalCode: {
          ...validatorRequiredIf(this.isFrance && !this.isAlreadyAssigned),
          ...validatorMinLength,
          ...validatorAlphaNum
        },
        discover: { ...validatorRequiredIf(!this.isAlreadyAssigned) },
        email: { ...validatorRequired, ...validatorEmail },
        phone: { ...validatorRequired, ...validatorNumeric },
        interests: { ...validatorRequired },
        hasBeenVdi: { ...validatorRequired }
      }
    }
  },
  computed: {
    discoverOptions() {
      return discoverOptions.map(option => {
        return { value: option, label: option }
      })
    },
    hasCountries() {
      return this.countries && this.countries.length
    },
    isFrance() {
      return Number(this.profile?.country) === FRANCE_COUNTRY_ID
    },
    hasStylistInStore() {
      const hasStylist = store.getPossibleSponsors()
      return !!hasStylist
    },
    disabledButton() {
      return this.v$.$invalid
    }
  },
  async created() {
    await this.loadCountriesList()
    this.loadContactInfo()
  },
  methods: {
    loadContactInfo() {
      const contactInfo = store.getContactInfo()
      this.profile = contactInfo
    },

    getMessage(property) {
      const error = this.v$.$errors.find(error => error.$property === property)
      return error?.$message ?? ''
    },
    async loadCountriesList() {
      try {
        const countries = await getCountries()
        this.countries = countries.map(country => {
          return { value: country.countryId, label: country.name }
        })
      } catch (error) {
        console.error(error)
      }
    },
    async validate() {
      this.loadingSubmit = true
      try {
        await store.submitProspectForm(this.profile, this.goToPage)
        this.toast.success('Formulaire envoyé !')
      } catch (error) {
        console.error(error)
        this.toast.error("Le formulaire n'a pas pu être envoyé")
      } finally {
        this.loadingSubmit = false
        this.addTracking()
      }
    },
    async goToPage(pageName) {
      if (this.isAlreadyAssigned) {
        this.showWebinarModal = true
      } else {
        await this.$router.push({ name: pageName })
      }
    },
    addTracking() {
      gaTrackByEvent('contactformSubmit', 'contact', 'prospect')
      if (window.fbq) {
        window.fbq('track', 'contactformSubmit')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.contact-form-header,
.contact-form {
  box-sizing: border-box;
  padding: 1.5rem;
}

.contact-form-header {
  text-align: center;
  line-height: 1.5em;
}

.contact-form {
  width: 100%;
  max-width: $breakpoint-s;
  margin: 0 auto;
}

.contact-form-error {
  color: $error;
  font-size: $font-size-s;
}
</style>
